// Node Modules
import { nanoid } from 'nanoid';
import { RichText } from 'prismic-reactjs';
import * as prismicR from '@prismicio/richtext'
import * as prismicH from '@prismicio/helpers'
import Button from '@/components/global-components/Button';

// Components
import Container from '@/components/global-components/Container';
import InformationCard from '@/components/global-components/information-card/InformationCard';
import StyledCard from '@/components/global-components/styled-card/StyledCard';

// Used to convert links into URLs.
const linkResolver = doc => `/${doc.uid}`

// An object mapping Rich Text block types to Markdown.
const markdownSerializer = prismicR.wrapMapSerializer({
  heading1: ({
    children, 
  }) =>
    `# ${children.join('')}\n\n`,
  heading2: ({
    children, 
  }) =>
    `## ${children.join('')}\n\n`,
  heading3: ({
    children, 
  }) =>
    `### ${children.join('')}\n\n`,
  heading4: ({
    children, 
  }) =>
    `#### ${children.join('')}\n\n`,
  heading5: ({
    children, 
  }) =>
    `##### ${children.join('')}\n\n`,
  heading6: ({
    children, 
  }) =>
    `###### ${children.join('')}\n\n`,
  paragraph: ({
    children, 
  }) =>
    `${children.join('')}\n\n`,
  preformatted: ({
    text, 
  }) =>
    `${text}`,
  strong: ({
    children, 
  }) =>
    `**${children.join('')}**`,
  em: ({
    children, 
  }) =>
    `_${children.join('')}_`,
  listItem: ({
    children, 
  }) =>
    `- ${children.join('')}\n`,
  oListItem: ({
    children, 
  }) =>
    `1. ${children.join('')}\n`,
  list: ({
    children, 
  }) =>
    `${children.join('')}\n`,
  oList: ({
    children, 
  }) =>
    `${children.join('')}\n`,
  image: ({
    node, 
  }) =>
    node.linkTo
      ? `[![${node.alt}](${node.url})](${node.linkTo.url})\n\n`
      : `![${node.alt}](${node.url})\n\n`,
  embed: ({
    node, 
  }) =>
    `${node.oembed.html}\n\n`,
  hyperlink: ({
    node,
    children, 
  }) =>
    `[${children.join('')}](${prismicH.asLink(node.data, linkResolver)})`,
  label: ({
    children, 
  }) =>
    children.join(''),
  span: ({
    text, 
  }) =>
    text.replace('\n', '<br/>'),
})

// Themes
import theme from '@/themes/index';

const AccommodationBody = ({
  content,
  isDesktop,
}) => {
  const getContainerStyle = index => {
    let styles = {
      gap: isDesktop ? '9.0625rem' : '1rem',
      flexDirection: isDesktop ? 'row' : 'column-reverse',
    };
    if (index % 2 !== 0) {
      styles = {
        ...styles,
        flexDirection: isDesktop ? 'row-reverse' : 'column-reverse',
      };
    }
    return styles;
  };

  const markdown1 = prismicR.serialize(content.privateMonteverdePrices, markdownSerializer).join('')
  const markdown2 = prismicR.serialize(content.privateFlamingoPrices, markdownSerializer).join('')
  const markdown3 = prismicR.serialize(content.privateHerediaPrices, markdownSerializer).join('')

  return (
    <>
      <section className='accommodation-types'>
        <Container style={{
          gap: isDesktop ? '5rem' : '2rem',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <h3>{content.title}</h3>
          <div className='alliances-description'>
            <RichText render={content.description} />
          </div>
          <div className='alliances-description'>
            <h4><strong>Homestay</strong></h4>
            <RichText render={content.homestayDescription} />
          </div>
          <StyledCard
            content={{
              'title': 'Homestay Monteverde',
              Typename: 'Style_card',
              image: content.homestayImagenMonteverde,
            }}
            isDesktop={isDesktop}
          >
            <InformationCard content={{
              'title': 'Homestay Monteverde',
              'cardContent': {
                Typename: 'Styled_card_content',
                'title': 'Homestay Monteverde',
                'description': content.homestayMonteverdeDescription,
              },
            }} showTitle={true} />
          </StyledCard>
          <StyledCard
            content={{
              'title': 'Homestay Monteverde',
              Typename: 'Style_card',
              image: content.homestayImagenFlamingo,
            }}
            containerStyle={{
              flexDirection: 'row-reverse', 
            }}  
            isDesktop={isDesktop}
          >
            <InformationCard content={{
              'title': 'Homestay Flamingo',
              'cardContent': {
                Typename: 'Styled_card_content',
                'title': 'Homestay Monteverde',
                'description': content.homestayFamingoDescription,
              },
            }} showTitle={true} />
          </StyledCard>
          <StyledCard
            content={{
              'title': 'Homestay Heredia',
              Typename: 'Style_card',
              image: content.homestayImagenHeredia,
            }}
            isDesktop={isDesktop}
          >
            <InformationCard content={{
              'title': 'Homestay Heredia',
              'cardContent': {
                Typename: 'Styled_card_content',
                'title': 'Homestay Heredia',
                'description': content.homestayHerediaDescription,
              },
            }} showTitle={true} />
          </StyledCard>
          <div className='alliances-description'>
            <h4><strong>Private Accommodation</strong></h4>
            <RichText render={content.privateAccommodationDescription} />
            <RichText render={content.privateMonteverdeDescription} />
            <div dangerouslySetInnerHTML={{
              __html: markdown1,
            }}></div>
            <Button
              text='Contact us'
              href='/contact'
              className='btn primary'
              target='_self'
              styles={{
                fontSize: '1rem', textAlign: 'center', maxWidth: '10rem', width: '100%', marginBottom: '2rem', 
              }}
            />
            <RichText render={content.privateFlamingoDescription} />
            <div dangerouslySetInnerHTML={{
              __html: markdown2,
            }}></div>
            <Button
              text='Contact us'
              href='/contact'
              className='btn primary'
              target='_self'
              styles={{
                fontSize: '1rem', textAlign: 'center', maxWidth: '10rem', width: '100%', marginBottom: '2rem', 
              }}
            />
            <RichText render={content.privateHerediaDescription} />
            <div dangerouslySetInnerHTML={{
              __html: markdown3,
            }}></div>
            <Button
              text='Contact us'
              href='/contact'
              className='btn primary'
              target='_self'
              styles={{
                fontSize: '1rem', textAlign: 'center', maxWidth: '10rem', width: '100%', marginBottom: '2rem', 
              }}
            />
          </div>
        </Container>
      </section>
      <style jsx>{`
        section.accommodation-types {
          padding: 5rem 2rem;
          display: flex;
          flex-direction: column;
        }

        section.room-types {
          padding: 5rem 2rem;
          display: flex;
          flex-direction: column;
          gap: 5rem;
        }

        @media only screen and (min-width: ${theme.breakpoints.desktopMin}) {
          section.accommodation-types {
            padding: 5rem 5rem;
          }

          section.room-types {
            padding: 5rem 5rem;
          }
        }
      `}</style>
    </>
  );
};

export default AccommodationBody;
